import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import TagBubbles from '../../components/admin/TagBubbles';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import ThumbnailDropzone from '../../components/admin/ThumbnailDropzone';
import * as bunnyLibrary from '../../services/adminBunnyLibrary.service';
import { formatDuration } from '../../utils/duration';

/**
 * A view into the Bunny Stream video library, right inside Team365 — so
 * Patti and Trish can find a replay, grab its link, fix its thumbnail, or
 * rename it — all without ever needing a Bunny.net login. Renaming here
 * changes the video's real title in Bunny itself (click the title to edit),
 * not just a local label — Jenn, 2026-08-31: wants this off her plate so
 * Patti/Trish can keep their own uploads organized.
 */
export default function AdminBunnyLibrary() {
  // ?q= opens the library already searched (links from Search, Oct 29).
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  // Topic tags (Jenn, 2026-10-13 + 2026-10-29): bubbles on each video, and a topic row to show only those videos.
  const [allTags, setAllTags] = useState([]);
  const [itemTags, setItemTags] = useState({});
  const [topic, setTopic] = useState(null);
  const loadAllTags = useCallback(() => {
    api.get('/admin/tags', { params: { kind: 'video' } }).then(r => setAllTags(r.data.tags)).catch(() => {});
  }, []);
  useEffect(() => { loadAllTags(); }, [loadAllTags]);
  const loadItemTags = async (items) => {
    if (!items?.length) { setItemTags({}); return; }
    try { setItemTags((await api.get('/admin/tags/items', { params: { kind: 'video', ids: items.map(v => v.id).join(',') } })).data.tags); }
    catch { /* bubbles just start empty */ }
  };
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState(null);
  // Members-only videos (Jenn, 2026-11-03): "Copy link" gives our /watch link, and each video has a
  // Members only / Public switch. Affiliate landing/confirmation videos are always public.
  const [publicIds, setPublicIds] = useState(new Set());
  const [affiliateIds, setAffiliateIds] = useState(new Set());
  useEffect(() => {
    api.get('/admin/videos/public').then(r => {
      setPublicIds(new Set(r.data.videos.map(v => v.video_id)));
      setAffiliateIds(new Set(r.data.affiliateVideoIds));
    }).catch(() => {});
  }, []);
  const togglePublic = async (id) => {
    const next = !publicIds.has(id);
    try {
      await api.put(`/admin/videos/${id}/public`, { public: next });
      setPublicIds(s => { const n = new Set(s); if (next) n.add(id); else n.delete(id); return n; });
    } catch (err) { setError(err.response?.data?.error || 'Could not change that video.'); }
  };
  const watchLink = (id) => `${window.location.origin}/watch/${id}`;

  // Click a picture to watch that video right here (Jenn, 2026-09-30).
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    if (!playing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPlaying(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [playing]);
  const [editingThumbId, setEditingThumbId] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addSourceUrl, setAddSourceUrl] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [blockedDelete, setBlockedDelete] = useState(null); // { id, usedIn }

  const load = useCallback(async (search, pageNum) => {
    try {
      setError(null);
      setData(null);
      const result = await bunnyLibrary.listVideos({ search, page: pageNum });
      setData(result);
      loadItemTags(result.items);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the video library.');
    }
  }, []);

  useEffect(() => { load(query, page); }, [load, page]);

  const showTopic = async (t) => {
    setTopic(t);
    setError(null);
    if (!t) { load(query, page); return; }
    setData(null);
    try {
      const { videos } = (await api.get('/admin/tags/videos', { params: { tag: t } })).data;
      const result = { items: videos, totalItems: videos.length, currentPage: 1, itemsPerPage: videos.length || 1 };
      setData(result);
      loadItemTags(videos);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the videos with that topic.');
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setTopic(null);
    setPage(1);
    load(query, 1);
  };

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(c => (c === id ? null : c)), 1500);
    } catch {
      window.prompt('Copy this link:', text);
    }
  };


  const saveTitle = async (videoId) => {
    if (!titleDraft.trim()) { setError('Title cannot be blank.'); return; }
    setSavingTitle(true);
    setError(null);
    try {
      const newTitle = titleDraft.trim();
      await bunnyLibrary.renameVideo(videoId, newTitle);
      setEditingTitleId(null);
      // Jenn, 2026-10-26: renames looked like they needed saving twice. Bunny saved the new title the first
      // time, but re-reading the list straight away still returned the old one for a few seconds. Show the
      // saved title right away instead of re-reading a list that hasn't caught up yet.
      setData(d => (d ? { ...d, items: d.items.map(x => (x.id === videoId ? { ...x, title: newTitle } : x)) } : d));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not rename that video.');
    } finally {
      setSavingTitle(false);
    }
  };

  const submitAddVideo = async (e) => {
    e.preventDefault();
    if (!addTitle.trim() || !addSourceUrl.trim()) return;
    setAddingVideo(true);
    setError(null);
    setAddResult(null);
    try {
      const result = await bunnyLibrary.addVideo(addTitle.trim(), addSourceUrl.trim());
      setAddResult(result.embedUrl);
      setAddTitle('');
      setAddSourceUrl('');
      load(query, page);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add that video.');
    } finally {
      setAddingVideo(false);
    }
  };

  const handleDelete = async (videoId) => {
    setDeletingId(videoId);
    setError(null);
    setBlockedDelete(null);
    try {
      await bunnyLibrary.deleteVideo(videoId);
      setConfirmDeleteId(null);
      load(query, page);
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.usedIn) {
        setBlockedDelete({ id: videoId, usedIn: data.usedIn });
        setConfirmDeleteId(null);
      } else {
        setError(data?.error || 'Could not delete that video.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.totalItems / data.itemsPerPage)) : 1;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />

        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl font-bold text-reset-brown">Video Library</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Everything hosted in Bunny — find a replay, copy its link, paste it as a resource anywhere in Team365. No Bunny login needed.
            </p>
          </div>
          <button
            onClick={() => { setShowAddVideo(v => !v); setAddResult(null); setError(null); }}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors shrink-0"
          >
            {showAddVideo ? 'Cancel' : '+ Add video'}
          </button>
        </div>

        {/* Hive Library (Jenn, 2026-10-24): member pictures/videos from My Hive, kept as their own folder — member content, not for use elsewhere. */}
        <Link
          to="/admin/hive-library"
          className="flex items-center gap-3 bg-white rounded-2xl shadow-sm px-4 py-3 mb-5 hover:shadow-md transition-shadow"
        >
          <span className="text-2xl" aria-hidden="true">📁</span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-reset-brown">Hive Library</span>
            <span className="block text-xs text-gray-500">Pictures and videos members shared in My Hive. Member content, not for use anywhere else.</span>
          </span>
          <span className="text-xs text-reset-terracotta font-medium shrink-0">Open →</span>
        </Link>

        {showAddVideo && (
          <form onSubmit={submitAddVideo} className="bg-white rounded-2xl shadow-sm p-4 mb-5 space-y-3">
            <p className="text-xs text-gray-500">
              Paste a direct link to the video file — a Google Drive "Copy link" share URL works for smaller files.
              This isn't tied to any event's replay; use it for a promo/announcement video or anything else you want a shareable link for.
            </p>
            <input
              value={addTitle} onChange={e => setAddTitle(e.target.value)}
              placeholder="Video title"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <input
              value={addSourceUrl} onChange={e => setAddSourceUrl(e.target.value)}
              placeholder="https://drive.google.com/file/d/... or any direct video URL"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            <button
              type="submit"
              disabled={addingVideo || !addTitle.trim() || !addSourceUrl.trim()}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {addingVideo ? 'Adding…' : 'Add video'}
            </button>
            {addResult && (
              <div className="bg-reset-sage/10 border border-reset-sage/30 rounded-lg p-3 text-xs text-reset-brown">
                Added — Bunny is processing it now (usually a few minutes before it's watchable). Its link:
                <div className="mt-1 font-mono break-all">{addResult}</div>
              </div>
            )}
          </form>
        )}

        <form onSubmit={submitSearch} className="flex gap-2 mb-5">
          <input
            value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search by title…"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
          >
            Search
          </button>
        </form>

        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-5">
            <span className="text-xs text-gray-500 mr-1">Topics:</span>
            {allTags.map(t => (
              <button key={t.tag} type="button" onClick={() => showTopic(topic === t.tag ? null : t.tag)}
                className={`rounded-full text-[11px] font-medium px-2.5 py-0.5 border transition-colors ${topic === t.tag ? 'bg-reset-sage text-white border-reset-sage' : 'bg-reset-sage/10 text-reset-sage border-transparent hover:border-reset-sage'}`}>
                {t.tag} ({t.n})
              </button>
            ))}
            {topic && <button type="button" onClick={() => showTopic(null)} className="text-[11px] text-gray-400 hover:text-reset-brown ml-1">Show all videos</button>}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {!data ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : data.items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            No videos found.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {data.items.map(v => (
              <div key={v.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setPlaying(v)}
                  aria-label={`Watch ${v.title}`}
                  className="group relative block w-full aspect-video bg-gray-100 focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                >
                  {v.thumbnailUrl && (
                    <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                  )}
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-reset-terracotta/90 text-white text-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform" aria-hidden="true">▶</span>
                  </span>
                </button>
                <div className="p-3">
                  {editingTitleId === v.id ? (
                    <div className="mb-1 space-y-1.5">
                      <input
                        autoFocus
                        value={titleDraft}
                        onChange={e => setTitleDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveTitle(v.id); if (e.key === 'Escape') setEditingTitleId(null); }}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
                      />
                      <div className="flex gap-2">
                        <button onClick={() => saveTitle(v.id)} disabled={savingTitle} className="text-xs font-medium text-reset-terracotta hover:underline disabled:opacity-50">
                          {savingTitle ? 'Saving…' : 'Save'}
                        </button>
                        <button onClick={() => setEditingTitleId(null)} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingTitleId(v.id); setTitleDraft(v.title); setError(null); }}
                      title="Rename this video"
                      className="w-full text-left mb-1 group"
                    >
                      <p className="text-sm font-medium text-reset-brown line-clamp-2 group-hover:text-reset-terracotta transition-colors">
                        {v.title} <span className="text-gray-300 group-hover:text-reset-terracotta">✎</span>
                      </p>
                    </button>
                  )}
                  {v.durationSeconds > 0 && (
                    <p className="text-xs text-gray-400 mb-2">{formatDuration(v.durationSeconds)}</p>
                  )}
                  <div className="mb-2">
                    <TagBubbles key={`${v.id}|${(itemTags[v.id] || []).join(',')}`} kind="video" id={v.id} title={v.title}
                      initial={itemTags[v.id] || []} allTags={allTags}
                      onSaved={(tags) => { setItemTags(m => ({ ...m, [v.id]: tags })); loadAllTags(); }} />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copy(v.id, watchLink(v.id))}
                      title="A members-only link: active members watch, everyone else sees how to join"
                      className="flex-1 px-2 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
                    >
                      {copiedId === v.id ? 'Copied!' : 'Copy link'}
                    </button>
                    <button
                      onClick={() => copy(`${v.id}-id`, v.id)}
                      title="Copy the raw Bunny video ID (for the Bunny Video ID field on a course lesson)"
                      className="px-2 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:border-reset-sage hover:text-reset-sage transition-colors"
                    >
                      {copiedId === `${v.id}-id` ? 'Copied!' : 'ID'}
                    </button>
                  </div>
                  {affiliateIds.has(v.id) ? (
                    <p className="mt-2 text-[11px] text-reset-sage">🔓 Public (affiliate page video)</p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => togglePublic(v.id)}
                      title={publicIds.has(v.id) ? 'Anyone with the link can watch. Tap to make it members only.' : 'Only active members can watch. Tap to make it public.'}
                      className={`mt-2 w-full px-2 py-1 text-[11px] font-medium rounded-lg border transition-colors ${publicIds.has(v.id) ? 'border-reset-sage text-reset-sage bg-reset-sage/10' : 'border-gray-200 text-gray-500 hover:border-reset-terracotta hover:text-reset-terracotta'}`}
                    >
                      {publicIds.has(v.id) ? '🔓 Public: anyone can watch' : '🔒 Members only'}
                    </button>
                  )}
                  {editingThumbId === v.id ? (
                    <div className="mt-2">
                      <ThumbnailDropzone
                        videoId={v.id}
                        onSaved={() => { setEditingThumbId(null); load(query, page); }}
                        onError={setError}
                        onCancel={() => setEditingThumbId(null)}
                      />
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center justify-between">
                      <button
                        onClick={() => { setEditingThumbId(v.id); setError(null); }}
                        className="text-xs font-medium text-gray-400 hover:text-reset-terracotta"
                      >
                        Set thumbnail
                      </button>
                      {confirmDeleteId === v.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(v.id)}
                            disabled={deletingId === v.id}
                            className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                          >
                            {deletingId === v.id ? 'Deleting…' : 'Confirm delete'}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs text-gray-400 hover:text-reset-brown"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setConfirmDeleteId(v.id); setBlockedDelete(null); }}
                          className="text-xs font-medium text-gray-400 hover:text-red-600"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                  {blockedDelete?.id === v.id && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-700">
                      Can't delete — still in use:
                      <ul className="list-disc list-inside mt-1">
                        {blockedDelete.usedIn.map((u, i) => (
                          <li key={i}>
                            {u.link ? <Link to={u.link} className="underline font-medium hover:text-red-900">{u.text}</Link> : u.text}
                          </li>
                        ))}
                      </ul>
                      Remove it from there first, then delete it here.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {data && totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="text-sm font-medium text-reset-terracotta hover:underline disabled:text-gray-300 disabled:no-underline"
            >
              ← Prev
            </button>
            <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="text-sm font-medium text-reset-terracotta hover:underline disabled:text-gray-300 disabled:no-underline"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {playing && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPlaying(null)}
          role="dialog"
          aria-modal="true"
          aria-label={playing.title}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <h2 className="text-sm font-semibold text-reset-brown truncate">{playing.title}</h2>
              <button type="button" onClick={() => setPlaying(null)} className="text-sm font-medium text-reset-terracotta hover:underline shrink-0">
                Close
              </button>
            </div>
            <div className="bg-black" style={{ height: 'min(75vh, 560px)' }}>
              <iframe
                key={playing.id}
                src={playing.embedUrl.includes('autoplay=')
                  ? playing.embedUrl.replace(/autoplay=(true|false)/, 'autoplay=true')
                  : `${playing.embedUrl}${playing.embedUrl.includes('?') ? '&' : '?'}autoplay=true`}
                title={playing.title}
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
